import React, { useEffect } from "react";

import { PlusOutlined } from "@ant-design/icons";
import UserIcon from "@src/assets/icons/profile.svg";
import { RoleListSkeleton } from "@src/features/resource_plan/components/role_list_skeleton";
import { mapRoleRequestData } from "@src/features/resource_plan/utils/resource_plan_table_utils";
import { useAgGrid } from "@src/hooks/use_ag_grid";
import {
  setCurrentTabConfig,
  setTotalDraftRequests,
} from "@src/services/slices/resourcePlanSlice";
import {
  projectsApi,
  useSaveRequestV2Mutation,
} from "@src/services/apis/projectsApi";
import { useLazySearchStandardRolesQuery } from "@src/services/apis/standardRolesApi";
import { RootState, useAppDispatch, useAppSelector } from "@src/setupStore";
import { Page, StandardRole, StandardRoleSearchRequest } from "@src/types";
import "./standard_role_list.less";
import {
  ResourcePlanTableRowData,
  RoleRequest,
} from "@src/types/role_request_types";
import { App, Button, List, Popover, Tooltip } from "antd";
import InfiniteScroll from "react-infinite-scroll-component";

interface StandardRoleListProps {
  projectId: string;
  onClose: () => void;
  searchTerm: string;
}

const StandardRoleList: React.FC<StandardRoleListProps> = ({
  projectId,
  onClose,
  searchTerm,
}) => {
  const { message } = App.useApp();
  const [loading, setLoading] = React.useState<boolean>(false);
  const { gridApi } = useAgGrid();

  const [standardRolePage, setStandardRolePage] = React.useState<
    Page<StandardRole>
  >({
    content: [],
    first: true,
  } as Page<StandardRole>);
  const [currentPage, setCurrentPage] = React.useState<number>(0);

  // Api
  const [saveRequest] = useSaveRequestV2Mutation();
  const [searchStandardRolesQuery] = useLazySearchStandardRolesQuery();

  // Redux state
  const { totalDraftRequests } = useAppSelector(
    (state: RootState) => state.resourcePlanSlice
  );
  const dispatch = useAppDispatch();

  useEffect(() => {
    setStandardRolePage(undefined);
    fetchStandardRoles(0).catch(console.error);
  }, [searchTerm]);

  async function fetchStandardRoles(pageNumber: number) {
    if (loading) return;
    setLoading(true);

    const request: StandardRoleSearchRequest = {
      filterParams: searchTerm ? { searchTerm } : {},
      pageRequestParams: {
        page: pageNumber,
        size: 50,
      },
    };

    try {
      const response: Page<StandardRole> = await searchStandardRolesQuery(
        request
      ).unwrap();
      setCurrentPage(response.number);

      setStandardRolePage((prev) => ({
        ...response,
        content: [...(prev?.content ?? []), ...response.content],
      }));
    } catch (error) {
      console.error("Error loading standard roles", error);
    }

    setLoading(false);
  }

  /**
   * Check if team lead is assigned to the role
   * @param {StandardRole} standardRole  Standard role to check if team lead is assigned
   */
  function isTeamLeadAssigned(standardRole: StandardRole): boolean {
    return !!standardRole.teamLeadFullName;
  }

  /**
   * Add role request as a draft
   * @param {StandardRole} standardRole   Standard role to be added as a role request
   */
  async function addRole(standardRole: StandardRole): Promise<void> {
    if (loading) return;
    setLoading(true);

    try {
      const savedRoleRequest: RoleRequest = await saveRequest({
        projectId: projectId,
        request: {
          genericRoleId: standardRole.standardRoleId,
        },
      }).unwrap();

      updateTableState(savedRoleRequest);
      updateCachedStates(savedRoleRequest);
      message.success("Request added successfully");
      setLoading(false);
      onClose();
    } catch (error) {
      console.error("Error saving request", error);
    }
  }

  /**
   * Update the table state with the new role request
   * @param {RoleRequest} roleRequest  Role request to be added to the table
   */
  function updateTableState(roleRequest: RoleRequest) {
    const mappedRoleRequest: ResourcePlanTableRowData[] = mapRoleRequestData([
      roleRequest,
    ]);

    gridApi.applyTransaction({ add: mappedRoleRequest, addIndex: 0 });
  }

  /**
   * Update the cache with the new role request.status
   * The cache was not revalidating via providesTags to keep it more performant.
   * That`s why we need to update the cache manually.
   *
   * @param {RoleRequest} roleRequest  Role request to be added to the cache
   */
  function updateCachedStates(roleRequest: RoleRequest): void {
    const updateCachedData: any = projectsApi.util.updateQueryData(
      "getRoleRequestsByProjectId",
      projectId,
      (roleRequests = []) => {
        return [roleRequest, ...roleRequests];
      }
    );

    // This is needed to internally update the state of the table. Because it might happen sometimes that when there are
    // sent requests but no draft requests that there is a problem with rendering the first element somehow.
    // I have not found a better solution because refreshCells and redrawRows does not work and leads also to performance issues
    // so that is why it is always forced to set the state to drafts.
    dispatch(
      setCurrentTabConfig({
        key: "drafts",
        label: "drafts",
      })
    );
    dispatch(setTotalDraftRequests(totalDraftRequests + 1));
    dispatch(updateCachedData);
  }

  return (
    <div className="creation-wrapper">
      {standardRolePage?.content?.length === 0 && (
        <div className="no-standard-roles-found">No standard roles found</div>
      )}
      <InfiniteScroll
        className="standard-role-list"
        height="90vh"
        dataLength={standardRolePage?.content.length}
        next={() => fetchStandardRoles(currentPage + 1)}
        hasMore={!standardRolePage?.last}
        loader={<RoleListSkeleton />}
      >
        <List
          className="standard-role-list__content"
          itemLayout="horizontal"
          dataSource={standardRolePage?.content || []}
          loading={loading}
          renderItem={(standardRole: StandardRole) => (
            <List.Item
              actions={[
                <Tooltip
                  key={standardRole.standardRoleId}
                  title={
                    !standardRole?.teamLeadFullName &&
                    "No team lead assigned to the role, contact administrator for further details"
                  }
                >
                  <Button
                    data-testid="add-role-button"
                    type="primary"
                    shape="circle"
                    icon={<PlusOutlined />}
                    disabled={!isTeamLeadAssigned(standardRole)}
                    onClick={() => addRole(standardRole)}
                  />
                </Tooltip>,
              ]}
            >
              <Popover
                key={standardRole.standardRoleId}
                placement="left"
                className="standard-role-item"
                title={standardRole.standardRoleName}
                overlayStyle={{ maxWidth: "500px", whiteSpace: "pre-line" }}
                content={standardRole?.description || "No description"}
              >
                <List.Item.Meta
                  title={standardRole.basicRole?.basicRoleName}
                  description={
                    <>
                      <div>
                        {standardRole.roleSpecification?.roleSpecificationName}
                      </div>
                      <div>
                        <UserIcon className="user-icon" />{" "}
                        {standardRole?.teamLeadFullName ??
                          "No Team Lead assigned"}
                      </div>
                    </>
                  }
                />
              </Popover>
            </List.Item>
          )}
        />
      </InfiniteScroll>
    </div>
  );
};

export default StandardRoleList;
